import { FGListen, FGSelectInput, FGTextInput, FieldGroup, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { ClubApi, ClubDto, CountryApi } from "../../../../api";
import { FGCountrySelect, SmallFormGenerator } from "../../../../components";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IClubAdressePageProps {
  clubId: number;
  editMode: boolean;
}

export const ClubAdressePage: React.FunctionComponent<IClubAdressePageProps> = ({ clubId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(ClubApi);
  const history = useHistory();
  const countryApi = useApiService(CountryApi);

  const [baseCountryId, baseCountryLoading] = useApiEffect(async () => await countryApi.getBelgianId(), []);

  const { data, loading, saveItem, saving } = useCrudApi<ClubDto>(
    {
      getApiFn: (id = clubId) => (id > 0 ? api.get(id) : new ClubDto().toJSON()),
      saveApiFn: (d) => api.save(d),
      onSavedRoute: (d) => d && `${ERoutes.club}/${d.clubId}/adresse/edit`,
      deleteApiFn: () => {},
    },
    [clubId]
  );

  const [clubPostalCode, setClubPostalCode] = React.useState("");
  const [terrainPostalCode, setTerrainPostalCode] = React.useState("");

  React.useEffect(() => {
    if (data && clubPostalCode === "") {
      setClubPostalCode(data.postalCode);
    }
  }, [data, clubPostalCode]);

  React.useEffect(() => {
    if (data && terrainPostalCode === "") {
      setTerrainPostalCode(data.terrainPostalCode);
    }
  }, [data, terrainPostalCode]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      number: Yup.string().typeError(t(ETLCodes.MustBeANumber)),
      terrainNumber: Yup.string().typeError(t(ETLCodes.MustBeANumber)),
      terrainCountryId: Yup.number().nullable(),
      countryId: Yup.number().nullable(),
    });
  }, [t]);

  const [postalCodes, pcloading] = useReferential((a) => a.getStringifiedBelgianPostalCodes(), true, []);
  const [clubLocalities, clubLocloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(clubPostalCode ?? ""),
    false,
    [clubPostalCode]
  );

  const [terrainLocalities, terrainLocloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(terrainPostalCode ?? ""),
    false,
    [terrainPostalCode]
  );

  const [isTerrainBelgium, setIsTerrainBelgium] = React.useState<boolean>(false);
  const [isClubBelgium, setClubIsBelgium] = React.useState<boolean>(false);

  return (
    data &&
    !baseCountryLoading && (
      <SmallFormGenerator
        initialValues={ClubDto.fromJS({
          ...data,
          terrainCity: data?.terrainCity?.toUpperCase(),
          city: data?.city?.toUpperCase(),
        }).toJSON()}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.club)}
        saving={saving}
      >
        <FGListen
          field="countryId"
          onChanged={(value, formik) => {
            if (value === baseCountryId) {
              setClubIsBelgium(true);
            } else {
              setClubIsBelgium(false);
            }
          }}
        />
        <FGListen
          field="postalCode"
          onChanged={(value) => {
            if (isClubBelgium && value) {
              setClubPostalCode(value);
            }
          }}
        />

        <FGListen
          field="terrainCountryId"
          onChanged={(value, formik) => {
            if (!value) {
              formik.setFieldValue("terrainCountryId", baseCountryId);
              setIsTerrainBelgium(true);
            } else {
              if (value === baseCountryId) {
                setIsTerrainBelgium(true);
              } else {
                setIsTerrainBelgium(false);
              }
            }
          }}
        />
        <FGListen
          field="terrainPostalCode"
          onChanged={(value) => {
            if (isTerrainBelgium && value) {
              setTerrainPostalCode(value);
            }
          }}
        />

        <FieldGroup fieldsetProps={{ title: t(ETLCodes.MailAddress) }}>
          <FGTextInput label={t(ETLCodes.Street)} name="street" />
          <FGTextInput label={t(ETLCodes.StreetNumber)} name="number" />
          <FieldGroup columns={2}>
            {isClubBelgium ? (
              <FGSelectInput name="postalCode" items={postalCodes} loading={pcloading} label={t(ETLCodes.PostalCode)} />
            ) : (
              <FGTextInput name="postalCode" label={t(ETLCodes.PostalCode)} />
            )}
          </FieldGroup>
          <FieldGroup columns={2}>
            {isClubBelgium ? (
              <FGSelectInput name="city" items={clubLocalities} loading={clubLocloading} label={t(ETLCodes.City)} />
            ) : (
              <FGTextInput name="city" label={t(ETLCodes.City)} />
            )}
          </FieldGroup>
          <FGCountrySelect label={t(ETLCodes.Country)} name="countryId" />
        </FieldGroup>
        <FieldGroup fieldsetProps={{ title: t(ETLCodes.TerrainAddress) }}>
          <FGTextInput label={t(ETLCodes.Street)} name="terrainStreet" />
          <FGTextInput label={t(ETLCodes.StreetNumber)} name="terrainNumber" />

          <FieldGroup columns={2}>
            {isTerrainBelgium ? (
              <FGSelectInput
                name="terrainPostalCode"
                items={postalCodes}
                loading={pcloading}
                label={t(ETLCodes.PostalCode)}
              />
            ) : (
              <FGTextInput name="terrainPostalCode" label={t(ETLCodes.PostalCode)} />
            )}
          </FieldGroup>
          <FieldGroup columns={2}>
            {isTerrainBelgium ? (
              <FGSelectInput
                name="terrainCity"
                items={terrainLocalities}
                loading={terrainLocloading}
                label={t(ETLCodes.City)}
              />
            ) : (
              <FGTextInput name="terrainCity" label={t(ETLCodes.City)} />
            )}
          </FieldGroup>
          <FGTextInput label={t(ETLCodes.Province)} name="terrainProvince" />
          <FGCountrySelect label={t(ETLCodes.Country)} name="terrainCountryId" />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
